import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getLogoUrl } from '../../../services/logoService';
import { exportSettingsService } from '../../../services/exportSettingsService';
import { useAuth } from '../../../contexts/AuthContext';

// ── Default settings ────────────────────────────────────────────────────────
const DEFAULT_SETTINGS = {
  exportFormat: 'pdf',
  viewType: 'gantt',
  taskDetail: 'all',
  ganttScale: 'weekly',
  // Gantt date range
  ganttDateRange: 'whole-project',
  ganttDateRangeCustomStart: '',
  ganttDateRangeCustomEnd: '',
  // Overview sub-fields
  includeOverview: true,
  overviewFields: {
    projectName: true,
    projectNumber: true,
    status: true,
    customer: true,
    startDate: true,
    endDate: true,
    budget: true,
    location: true,
    description: false,
  },
  // Phases
  includePhases: true,
  phaseFields: {
    phaseName: true,
    phaseDates: true,
    phaseStatus: true,
  },
  // Deliverables
  deliverableFields: {
    name: true,
    status: true,
    startDate: true,
    dueDate: true,
    assignedResources: false,
    phase: true,
  },
  // Gantt chart columns
  ganttColumns: {
    name: true,
    startDate: true,
    endDate: true,
    status: true,
    phase: true,
    assignedResources: false,
    duration: false,
    percentComplete: false,
  },
  // Gantt bar colors
  ganttColors: {
    phase: '#166534',
    deliverable: '#16a34a',
    task: '#fb923c',
    subtask: '#a78bfa',
  },
};

const ExportModal = ({ isOpen, onClose, project, phases, deliverables }) => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [isExporting, setIsExporting] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const saveTimerRef = useRef(null);

  // ── Preset state ───────────────────────────────────────────────────────────
  const [presets, setPresets] = useState([]);
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [isSavingPreset, setIsSavingPreset] = useState(false);
  const [presetsLoaded, setPresetsLoaded] = useState(false);

  // Brand green color matching the site logo
  const BRAND_GREEN = '#166534';
  const BRAND_GREEN_LIGHT = '#bbf7d0';
  const BRAND_GREEN_DARK = '#14532d';

  // ── Load persisted settings on open ───────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    setSettingsLoaded(false);
    setPreviewUrl(null);
    setShowPreview(false);
    setShowSavePreset(false);
    setPresetName('');

    const load = async () => {
      if (user?.id && project?.id) {
        const { data } = await exportSettingsService?.getSettings(user?.id, project?.id);
        if (data) {
          setSettings((prev) => ({ ...DEFAULT_SETTINGS, ...prev, ...data }));
        }
      }
      setSettingsLoaded(true);
    };
    load();

    // Load presets
    const loadPresets = async () => {
      if (user?.id) {
        const { data } = await exportSettingsService?.getPresets(user?.id);
        setPresets(data || []);
        setPresetsLoaded(true);
      }
    };
    loadPresets();
  }, [isOpen, user?.id, project?.id]);

  // ── Auto-save settings with debounce ──────────────────────────────────────
  const persistSettings = useCallback(
    (newSettings) => {
      if (!user?.id || !project?.id || !settingsLoaded) return;
      if (saveTimerRef?.current) clearTimeout(saveTimerRef?.current);
      saveTimerRef.current = setTimeout(() => {
        exportSettingsService?.saveSettings(user?.id, project?.id, newSettings);
      }, 600);
    },
    [user?.id, project?.id, settingsLoaded]
  );

  const updateSettings = useCallback(
    (patch) => {
      setSettings((prev) => {
        const next = { ...prev, ...patch };
        persistSettings(next);
        return next;
      });
    },
    [persistSettings]
  );

  const updateNestedField = useCallback(
    (group, field, value) => {
      setSettings((prev) => {
        const next = { ...prev, [group]: { ...prev?.[group], [field]: value } };
        persistSettings(next);
        return next;
      });
    },
    [persistSettings]
  );

  // ── Apply a preset ─────────────────────────────────────────────────────────
  const applyPreset = useCallback((preset) => {
    const merged = { ...DEFAULT_SETTINGS, ...preset?.settings };
    setSettings(merged);
    persistSettings(merged);
  }, [persistSettings]);

  // ── Save current settings as a new preset ─────────────────────────────────
  const handleSavePreset = async () => {
    if (!presetName?.trim() || !user?.id) return;
    setIsSavingPreset(true);
    try {
      const { data, error } = await exportSettingsService?.savePreset(user?.id, presetName?.trim(), settings);
      if (!error && data) {
        setPresets((prev) => [...prev, data]);
        setPresetName('');
        setShowSavePreset(false);
      }
    } finally {
      setIsSavingPreset(false);
    }
  };

  // ── Delete a preset ────────────────────────────────────────────────────────
  const handleDeletePreset = async (presetId) => {
    if (!user?.id) return;
    const { error } = await exportSettingsService?.deletePreset(presetId, user?.id);
    if (!error) {
      setPresets((prev) => prev?.filter((p) => p?.id !== presetId));
    }
  };

  if (!isOpen) return null;

  const {
    exportFormat, viewType, taskDetail, ganttScale,
    includeOverview, overviewFields, includePhases, phaseFields, deliverableFields,
    ganttColors, ganttColumns,
    ganttDateRange, ganttDateRangeCustomStart, ganttDateRangeCustomEnd,
  } = settings;

  const formatOptions = [
    { value: 'pdf', label: 'PDF', icon: 'FileText', description: 'Portable document format' },
    { value: 'image', label: 'Image', icon: 'Image', description: 'PNG image file' },
    { value: 'excel', label: 'Excel', icon: 'Table', description: 'Spreadsheet (.xlsx)' },
  ];

  const viewOptions = [
    { value: 'gantt', label: 'Gantt Chart', icon: 'BarChart3', description: 'Timeline bar chart view' },
    { value: 'gantt-resources', label: 'Gantt & Resources', icon: 'Users', description: 'Gantt with resource allocation' },
    { value: 'project-detail', label: 'Project Detail', icon: 'Table', description: 'Full spreadsheet detail view' },
    { value: 'summary', label: 'Summary View', icon: 'FileText', description: 'High-level project summary' },
  ];

  const scaleOptions = [
    { value: 'daily', label: 'Daily', description: 'One column per day' },
    { value: 'weekly', label: 'Weekly', description: 'One column per week' },
    { value: 'monthly', label: 'Monthly', description: 'One column per month' },
  ];

  // ── Helpers ────────────────────────────────────────────────────────────────
  const parseDate = (str) => {
    if (!str) return null;
    let d = new Date(str);
    return isNaN(d?.getTime()) ? null : d;
  };

  const formatDateShort = (str) => {
    let d = parseDate(str);
    if (!d) return 'N/A';
    return d?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  };

  const buildHierarchy = () => {
    const allItems = deliverables || [];
    const topDeliverables = allItems?.filter((d) => !d?.rowType || d?.rowType === 'deliverable');
    const tasks = allItems?.filter((d) => d?.rowType === 'task');
    const subtasks = allItems?.filter((d) => d?.rowType === 'subtask');
    const rows = [];
    topDeliverables?.forEach((del) => {
      rows?.push({ item: del, indent: 0, phaseId: del?.phaseId });
      if (taskDetail === 'all') {
        const myTasks = tasks?.filter((t) => t?.parentId === del?.id);
        myTasks?.forEach((task) => {
          rows?.push({ item: task, indent: 1, phaseId: del?.phaseId });
          const mySubs = subtasks?.filter((s) => s?.parentId === task?.id);
          mySubs?.forEach((sub) => rows?.push({ item: sub, indent: 2, phaseId: del?.phaseId }));
        });
      }
    });
    return rows;
  };

  const computeDateRange = () => {
    const allItems = deliverables || [];
    const allPhases = phases || [];
    const dates = [];
    allPhases?.forEach((p) => {
      if (p?.startDate) dates?.push(parseDate(p?.startDate));
      if (p?.endDate) dates?.push(parseDate(p?.endDate));
    });
    allItems?.forEach((d) => {
      if (d?.startDate) dates?.push(parseDate(d?.startDate));
      if (d?.endDate) dates?.push(parseDate(d?.endDate));
    });
    if (project?.startDate) dates?.push(parseDate(project?.startDate));
    if (project?.endDate) dates?.push(parseDate(project?.endDate));
    const valid = dates?.filter(Boolean);
    if (valid?.length === 0) return { minDate: new Date(), maxDate: new Date(), totalDays: 1 };
    let minDate = new Date(Math.min(...valid.map((d) => d.getTime())));
    let maxDate = new Date(Math.max(...valid.map((d) => d.getTime())));
    minDate?.setDate(minDate?.getDate() - 2);
    maxDate?.setDate(maxDate?.getDate() + 2);
    const totalDays = Math.max(1, Math.round((maxDate - minDate) / 86400000));
    return { minDate, maxDate, totalDays };
  };

  // ── Gantt-specific date range (respects the user's range selection) ────────
  const computeGanttDateRange = () => {
    const { minDate: projectMin, maxDate: projectMax } = computeDateRange();
    const today = new Date();
    today?.setHours(0, 0, 0, 0);

    let minDate, maxDate;

    if (ganttDateRange === '30-days') {
      minDate = new Date(today);
      maxDate = new Date(today);
      maxDate?.setDate(maxDate?.getDate() + 30);
    } else if (ganttDateRange === '60-days') {
      minDate = new Date(today);
      maxDate = new Date(today);
      maxDate?.setDate(maxDate?.getDate() + 60);
    } else if (ganttDateRange === '90-days') {
      minDate = new Date(today);
      maxDate = new Date(today);
      maxDate?.setDate(maxDate?.getDate() + 90);
    } else if (ganttDateRange === 'custom') {
      const cs = parseDate(ganttDateRangeCustomStart);
      const ce = parseDate(ganttDateRangeCustomEnd);
      minDate = cs || projectMin;
      maxDate = ce || projectMax;
    } else {
      // whole-project (default)
      minDate = projectMin;
      maxDate = projectMax;
    }

    // Ensure minDate <= maxDate
    if (minDate > maxDate) {
      const tmp = minDate;
      minDate = maxDate;
      maxDate = tmp;
    }

    const totalDays = Math.max(1, Math.round((maxDate - minDate) / 86400000));
    return { minDate, maxDate, totalDays };
  };

  const buildExportData = () => {
    const data = { project: project || {}, phases: [], hierarchy: buildHierarchy() };
    if (includePhases && phases?.length > 0) {
      data.phases = phases?.map((p) => ({
        id: p?.id,
        name: p?.name || '',
        startDate: p?.startDate || '',
        endDate: p?.endDate || '',
        status: p?.status || '',
      }));
    }
    return data;
  };

  const ganttBarProps = (startStr, endStr, minDate, totalDays, barAreaWidth) => {
    let s = parseDate(startStr);
    const e = parseDate(endStr);
    if (!s || !e) return null;
    const offsetDays = Math.round((s - minDate) / 86400000);
    const durationDays = Math.max(1, Math.round((e - s) / 86400000) + 1);
    // Check if bar is completely outside the visible range
    if (offsetDays >= totalDays || offsetDays + durationDays <= 0) return null;
    const clampedOffset = Math.max(0, offsetDays);
    const clampedEnd = Math.min(totalDays, offsetDays + durationDays);
    const clampedDuration = clampedEnd - clampedOffset;
    const x = (clampedOffset / totalDays) * barAreaWidth;
    const w = Math.max(4, (clampedDuration / totalDays) * barAreaWidth);
    return { x, w };
  };

  // ── Preview generation (canvas-based) ─────────────────────────────────────
  const generatePreview = async () => {
    setIsGeneratingPreview(true);
    try {
      const data = buildExportData();
      const isGantt = viewType === 'gantt' || viewType === 'gantt-resources';
      const { minDate, totalDays } = computeGanttDateRange();

      const padding = 20;

      // Build active gantt columns list
      const activeGanttCols = isGantt ? [
        ganttColumns?.name !== false && { key: 'name', label: 'Name', w: 120 },
        ganttColumns?.startDate && { key: 'startDate', label: 'Start', w: 60 },
        ganttColumns?.endDate && { key: 'endDate', label: 'End', w: 60 },
        ganttColumns?.status && { key: 'status', label: 'Status', w: 60 },
        ganttColumns?.phase && { key: 'phase', label: 'Phase', w: 70 },
        ganttColumns?.assignedResources && { key: 'resources', label: 'Resources', w: 70 },
        ganttColumns?.duration && { key: 'duration', label: 'Duration', w: 50 },
        ganttColumns?.percentComplete && { key: 'pct', label: '% Done', w: 45 },
      ]?.filter(Boolean) : [];

      const labelColW = isGantt ? activeGanttCols?.reduce((s, c) => s + c?.w, 0) : 200;
      const MAX_COLS = 32;
      const tickIntervalDays = ganttScale === 'daily' ? 1 : ganttScale === 'monthly' ? 30 : 7;
      const totalTicks = Math.ceil(totalDays / tickIntervalDays);
      const numSections = isGantt ? Math.ceil(totalTicks / MAX_COLS) : 1;
      const ticksPerSection = Math.ceil(totalTicks / numSections);
      const daysPerSection = ticksPerSection * tickIntervalDays;

      const barAreaW = 520;
      const canvasWidth = padding * 2 + labelColW + barAreaW;
      const rowH = 18;
      const barH = 10;
      const headerSectionH = 52;
      const ganttHeaderH = 42;

      const phaseRows = data?.phases?.length || 0;
      const itemRows = data?.hierarchy?.length;
      const totalRows = phaseRows + itemRows;

      let overviewH = includeOverview ? 130 : 0;
      // Two-line phase rows: ~30px each (name + info line) + section header
      let phasesH = includePhases && data?.phases?.length > 0 ? data?.phases?.length * 30 + 32 : 0;
      // Each gantt section has a header + all rows
      const ganttSectionH = isGantt ? (ganttHeaderH + totalRows * (rowH + 2) + 10) : 0;
      const ganttH = isGantt ? ganttSectionH * numSections + 40 : 0;
      const listH = !isGantt ? itemRows * 22 + 32 : 0;

      const canvasHeight = headerSectionH + overviewH + phasesH + ganttH + listH + padding * 2;

      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = Math.max(canvasHeight, 200);
      const ctx = canvas?.getContext('2d');

      ctx.fillStyle = '#f8fafc';
      ctx?.fillRect(0, 0, canvasWidth, canvas?.height);

      // Header bar — white with bottom border (matches preview style)
      ctx.fillStyle = '#ffffff';
      ctx?.fillRect(0, 0, canvasWidth, headerSectionH);
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 1;
      ctx?.beginPath();
      ctx?.moveTo(0, headerSectionH);
      ctx?.lineTo(canvasWidth, headerSectionH);
      ctx?.stroke();

      try {
        const logoUrl = await getLogoUrl();
        if (logoUrl) {
          await new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              const logoH = 44;
              const logoW = (img.width / img.height) * logoH;
              ctx.drawImage(img, padding, (headerSectionH - logoH) / 2, logoW, logoH);
              resolve();
            };
            img.onerror = resolve;
            img.src = logoUrl;
          });
        }
      } catch {}

      ctx.fillStyle = '#111827';
      ctx.font = 'bold 20px Arial, sans-serif';
      ctx?.fillText(data?.project?.projectName || 'Project Export', padding + 60, 36);
      ctx.font = '13px Arial, sans-serif';
      ctx.fillStyle = '#6b7280';
      ctx?.fillText(`View: ${viewOptions?.find((v) => v?.value === viewType)?.label || viewType}  |  Exported: ${new Date()?.toLocaleDateString()}`, padding + 60, 56);

      let cy = headerSectionH + padding;

      const drawSectionHeader = (title) => {
        ctx.fillStyle = BRAND_GREEN;
        ctx?.fillRect(padding, cy, canvasWidth - padding * 2, 20);
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 11px Arial, sans-serif';
        ctx?.fillText(title, padding + 6, cy + 14);
        cy += 24;
      };

      if (includeOverview) {
        drawSectionHeader('PROJECT OVERVIEW');
        const p = data?.project;
        const fields = [
          overviewFields?.projectName && ['Project Name', p?.projectName],
          overviewFields?.projectNumber && ['Project #', p?.projectNumber],
          overviewFields?.status && ['Status', p?.status],
          overviewFields?.customer && ['Customer', p?.customerName],
          overviewFields?.startDate && ['Start Date', p?.startDate],
          overviewFields?.endDate && ['End Date', p?.endDate],
          overviewFields?.budget && ['Budget', p?.totalBudget ? `$${Number(p?.totalBudget)?.toLocaleString()}` : 'N/A'],
          overviewFields?.location && ['Location', p?.location],
          overviewFields?.description && ['Description', p?.description],
        ]?.filter(Boolean);
        fields?.forEach(([label, val]) => {
          ctx.font = 'bold 10px Arial, sans-serif';
          ctx.fillStyle = '#374151';
          ctx?.fillText(`${label}:`, padding + 4, cy + 12);
          ctx.font = '10px Arial, sans-serif';
          ctx.fillStyle = '#1e293b';
          ctx?.fillText(String(val || 'N/A'), padding + 90, cy + 12);
          ctx.strokeStyle = '#e2e8f0';
          ctx.lineWidth = 0.5;
          ctx?.beginPath();
          ctx?.moveTo(padding, cy + 15);
          ctx?.lineTo(canvasWidth - padding, cy + 15);
          ctx?.stroke();
          cy += 14;
        });
        cy += 8;
      }

      if (includePhases && data?.phases?.length > 0) {
        drawSectionHeader('PHASES');
        data?.phases?.forEach((ph, i) => {
          // Line 1: phase name (bold)
          ctx.font = 'bold 10px Arial, sans-serif';
          ctx.fillStyle = BRAND_GREEN_DARK;
          ctx?.fillText(`${i + 1}. ${ph?.name}`, padding + 4, cy + 11);
          // Line 2: dates / status (smaller, muted) — only if there's info to show
          const phInfo = [
            phaseFields?.phaseDates && `${formatDateShort(ph?.startDate)} → ${formatDateShort(ph?.endDate)}`,
            phaseFields?.phaseStatus && ph?.status,
          ]?.filter(Boolean)?.join('  |  ');
          if (phInfo) {
            ctx.font = '9px Arial, sans-serif';
            ctx.fillStyle = '#64748b';
            ctx?.fillText(phInfo, padding + 14, cy + 22);
          }
          ctx.strokeStyle = '#e2e8f0';
          ctx.lineWidth = 0.5;
          ctx?.beginPath();
          ctx?.moveTo(padding, cy + 26);
          ctx?.lineTo(canvasWidth - padding, cy + 26);
          ctx?.stroke();
          cy += phInfo ? 28 : 18;
        });
        cy += 8;
      }

      if (isGantt) {
        const ganttLeft = padding + labelColW;
        // ganttW is fixed to barAreaW — content must not exceed this
        const ganttW = barAreaW;
        // Total content width for this gantt section (label cols + bar area)
        const ganttContentW = labelColW + ganttW;

        const getItemColValue = (item, colKey, phaseId) => {
          switch (colKey) {
            case 'name': return item?.name || item?.title || 'Unnamed';
            case 'startDate': return formatDateShort(item?.startDate);
            case 'endDate': return formatDateShort(item?.endDate || item?.dueDate);
            case 'status': return item?.status || '';
            case 'phase': {
              const ph = (phases || [])?.find((p) => p?.id === (item?.phaseId || phaseId));
              return ph?.name || '';
            }
            case 'resources': return (item?.assignedResources || [])?.map((r) => r?.name || r)?.join(', ') || '';
            case 'duration': {
              let s = parseDate(item?.startDate);
              const e = parseDate(item?.endDate || item?.dueDate);
              if (s && e) return `${Math.max(1, Math.round((e - s) / 86400000) + 1)}d`;
              return '';
            }
            case 'pct': return item?.percentComplete != null ? `${item?.percentComplete}%` : '';
            default: return '';
          }
        };

        // Render one gantt section (sectionIdx 0-based)
        const renderGanttSection = (sectionIdx) => {
          const sectionStartDay = sectionIdx * daysPerSection;
          const sectionEndDay = Math.min(totalDays, sectionStartDay + daysPerSection);
          const sectionDays = sectionEndDay - sectionStartDay;
          const sectionMinDate = new Date(minDate.getTime() + sectionStartDay * 86400000);

          // Section label if multiple sections
          if (numSections > 1) {
            const secLabel = `GANTT CHART — Section ${sectionIdx + 1} of ${numSections}`;
            drawSectionHeader(secLabel);
          } else {
            drawSectionHeader('GANTT CHART');
          }

          // Draw column header row
          ctx.fillStyle = '#f1f5f9';
          ctx?.fillRect(padding, cy, labelColW, ganttHeaderH);
          ctx.fillStyle = '#e2e8f0';
          ctx?.fillRect(ganttLeft, cy, ganttW, ganttHeaderH);
          ctx.strokeStyle = BRAND_GREEN;
          ctx.lineWidth = 0.8;
          ctx?.strokeRect(ganttLeft, cy, ganttW, ganttHeaderH);

          // Draw column headers
          let colX = padding;
          activeGanttCols?.forEach((col) => {
            ctx.font = 'bold 8px Arial, sans-serif';
            ctx.fillStyle = '#374151';
            ctx?.fillText(col?.label, colX + 3, cy + ganttHeaderH / 2 + 3);
            ctx.strokeStyle = '#cbd5e1';
            ctx.lineWidth = 0.5;
            ctx?.beginPath();
            ctx?.moveTo(colX + col?.w, cy);
            ctx?.lineTo(colX + col?.w, cy + ganttHeaderH);
            ctx?.stroke();
            colX += col?.w;
          });

          // Draw 3-row timeline header: Year / Month / Day
          const subRowH = ganttHeaderH / 3;
          [1, 2]?.forEach((i) => {
            ctx.strokeStyle = '#cbd5e1';
            ctx.lineWidth = 0.4;
            ctx?.beginPath();
            ctx?.moveTo(ganttLeft, cy + subRowH * i);
            ctx?.lineTo(ganttLeft + ganttW, cy + subRowH * i);
            ctx?.stroke();
          });

          const yearSpans = {};
          const monthSpans = {};
          for (let d = 0; d < sectionDays; d += tickIntervalDays) {
            const tickDate = new Date(sectionMinDate.getTime() + d * 86400000);
            const yr = tickDate?.getFullYear();
            const mo = `${yr}-${tickDate?.getMonth()}`;
            const xTick = ganttLeft + (d / sectionDays) * ganttW;
            if (!yearSpans?.[yr]) yearSpans[yr] = { x: xTick, label: String(yr) };
            if (!monthSpans?.[mo]) monthSpans[mo] = { x: xTick, label: tickDate?.toLocaleDateString('en-US', { month: 'short' }) };
          }

          // Draw vertical tick lines + day numbers (row 3)
          for (let d = 0; d < sectionDays; d += tickIntervalDays) {
            const tickDate = new Date(sectionMinDate.getTime() + d * 86400000);
            const xTick = ganttLeft + (d / sectionDays) * ganttW;
            ctx.strokeStyle = '#cbd5e1';
            ctx.lineWidth = 0.3;
            ctx?.beginPath();
            ctx?.moveTo(xTick, cy + subRowH * 2);
            ctx?.lineTo(xTick, cy + ganttHeaderH);
            ctx?.stroke();
            ctx.font = '7px Arial, sans-serif';
            ctx.fillStyle = '#475569';
            ctx?.fillText(String(tickDate?.getDate())?.padStart(2, '0'), xTick + 1, cy + subRowH * 2 + subRowH * 0.65);
          }

          ctx.font = 'bold 8px Arial, sans-serif';
          ctx.fillStyle = '#1e293b';
          Object.values(yearSpans)?.forEach(({ x, label }) => {
            ctx?.fillText(label, x + 2, cy + subRowH * 0.7);
          });

          ctx.font = '7px Arial, sans-serif';
          ctx.fillStyle = '#374151';
          Object.values(monthSpans)?.forEach(({ x, label }) => {
            ctx?.fillText(label, x + 2, cy + subRowH + subRowH * 0.65);
          });

          cy += ganttHeaderH;

          // Clip the entire gantt rows area to prevent any bleed past the header right edge
          ctx?.save();
          ctx?.beginPath();
          ctx?.rect(padding, cy, ganttContentW, canvasHeight);
          ctx?.clip();

          const renderGanttRows = (rows, currentPhaseId) => {
            rows?.forEach(({ item, indent, phaseId: rowPhaseId }) => {
              const usedPhaseId = rowPhaseId || currentPhaseId;
              const indentPx = indent * 10;
              ctx.fillStyle = indent % 2 === 0 ? '#f8fafc' : '#f1f5f9';
              // Row background only covers label + bar area (no bleed past right edge)
              ctx?.fillRect(padding, cy, ganttContentW, rowH);

              let cx2 = padding;
              activeGanttCols?.forEach((col) => {
                const cellVal = col?.key === 'name' ? (item?.name || item?.title || 'Unnamed')
                  : getItemColValue(item, col?.key, usedPhaseId);
                ctx.font = (col?.key === 'name' && indent === 0) ? 'bold 8px Arial, sans-serif' : '8px Arial, sans-serif';
                ctx.fillStyle = indent === 2 ? '#6b7280' : '#1e293b';
                const textX = cx2 + (col?.key === 'name' ? 3 + indentPx : 3);
                ctx?.save();
                ctx?.beginPath();
                ctx?.rect(cx2, cy, col?.w - 2, rowH);
                ctx?.clip();
                ctx?.fillText(cellVal, textX, cy + rowH / 2 + 3);
                ctx?.restore();
                ctx.strokeStyle = '#e2e8f0';
                ctx.lineWidth = 0.3;
                ctx?.beginPath();
                ctx?.moveTo(cx2 + col?.w, cy);
                ctx?.lineTo(cx2 + col?.w, cy + rowH);
                ctx?.stroke();
                cx2 += col?.w;
              });

              // Gantt bar — clipped to this section's date range
              const bp = ganttBarProps(item?.startDate, item?.endDate || item?.dueDate, sectionMinDate, sectionDays, ganttW);
              if (bp) {
                ctx.fillStyle = indent === 0 ? (ganttColors?.deliverable || '#16a34a') : indent === 1 ? (ganttColors?.task || '#fb923c') : (ganttColors?.subtask || '#a78bfa');
                ctx?.save();
                ctx?.beginPath();
                ctx?.rect(ganttLeft, cy, ganttW, rowH);
                ctx?.clip();
                ctx?.beginPath();
                ctx?.roundRect(ganttLeft + bp?.x, cy + (rowH - barH) / 2, bp?.w, barH, 2);
                ctx?.fill();
                ctx?.restore();
              }
              ctx.strokeStyle = '#e2e8f0';
              ctx.lineWidth = 0.3;
              ctx?.beginPath();
              ctx?.moveTo(padding, cy + rowH);
              ctx?.lineTo(padding + ganttContentW, cy + rowH);
              ctx?.stroke();
              cy += rowH + 2;
            });
          };

          if (data?.phases?.length > 0) {
            data?.phases?.forEach((ph) => {
              ctx.fillStyle = BRAND_GREEN_LIGHT;
              ctx?.fillRect(padding, cy, ganttContentW, rowH);
              ctx.font = 'bold 10px Arial, sans-serif';
              ctx.fillStyle = BRAND_GREEN_DARK;
              ctx?.fillText(ph?.name, padding + 4, cy + rowH / 2 + 3);
              const bp = ganttBarProps(ph?.startDate, ph?.endDate, sectionMinDate, sectionDays, ganttW);
              if (bp) {
                ctx.fillStyle = ganttColors?.phase || BRAND_GREEN;
                ctx?.save();
                ctx?.beginPath();
                ctx?.rect(ganttLeft, cy, ganttW, rowH);
                ctx?.clip();
                ctx?.beginPath();
                ctx?.roundRect(ganttLeft + bp?.x, cy + (rowH - barH) / 2, bp?.w, barH, 2);
                ctx?.fill();
                ctx?.restore();
              }
              ctx.strokeStyle = BRAND_GREEN;
              ctx.lineWidth = 0.5;
              ctx?.beginPath();
              ctx?.moveTo(padding, cy + rowH);
              ctx?.lineTo(padding + ganttContentW, cy + rowH);
              ctx?.stroke();
              cy += rowH + 2;

              const phaseItems = data?.hierarchy?.filter((r) => r?.phaseId === ph?.id || r?.item?.phaseId === ph?.id);
              renderGanttRows(phaseItems, ph?.id);
            });
            // Render items not assigned to any phase
            const phaseIds = new Set(data?.phases?.map((p) => p?.id));
            const unassignedRows = data?.hierarchy?.filter((r) => {
              const pid = r?.phaseId || r?.item?.phaseId;
              return !pid || !phaseIds?.has(pid);
            });
            if (unassignedRows?.length > 0) {
              ctx.fillStyle = '#f1f5f9';
              ctx?.fillRect(padding, cy, ganttContentW, rowH);
              ctx.font = 'bold 10px Arial, sans-serif';
              ctx.fillStyle = '#64748b';
              ctx?.fillText('Unassigned', padding + 4, cy + rowH / 2 + 3);
              cy += rowH + 2;
              renderGanttRows(unassignedRows, null);
            }
          } else {
            renderGanttRows(data?.hierarchy, null);
          }

          // Restore clip after all rows
          ctx?.restore();

          cy += 8;
        };

        for (let s = 0; s < numSections; s++) {
          renderGanttSection(s);
        }

      } else {
        drawSectionHeader('DELIVERABLES');
        data?.hierarchy?.forEach(({ item, indent }) => {
          const indentPx = indent * 16;
          const prefix = indent === 0 ? '▸ ' : indent === 1 ? '– ' : '· ';
          ctx.font = indent === 0 ? 'bold 10px Arial, sans-serif' : '10px Arial, sans-serif';
          ctx.fillStyle = indent === 2 ? '#6b7280' : '#1e293b';
          ctx?.fillText(`${prefix}${item?.name || item?.title || 'Unnamed'}`, padding + 4 + indentPx, cy + 12);
          const details = [
            deliverableFields?.status && `Status: ${item?.status || 'N/A'}`,
            deliverableFields?.dueDate && `Due: ${formatDateShort(item?.dueDate || item?.endDate)}`,
          ]?.filter(Boolean)?.join('  |  ');
          if (details) {
            ctx.font = '9px Arial, sans-serif';
            ctx.fillStyle = '#94a3b8';
            ctx?.fillText(details, padding + 6 + indentPx, cy + 20);
          }
          cy += 22;
        });
      }

      setPreviewUrl(canvas?.toDataURL('image/png'));
    } catch (err) {
      console.error('Preview generation error:', err);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  // ── PDF Export ─────────────────────────────────────────────────────────────
  const exportAsPDF = async (data) => {
    try {
      const { jsPDF } = await import('jspdf');
      const isGantt = viewType === 'gantt' || viewType === 'gantt-resources';
      const orientation = isGantt ? 'landscape' : 'portrait';
      const doc = new jsPDF({ orientation, unit: 'mm', format: 'a4' });
      const pageWidth = doc?.internal?.pageSize?.getWidth();
      const pageHeight = doc?.internal?.pageSize?.getHeight();
      const margin = 12;
      const contentWidth = pageWidth - margin * 2;
      let y = margin;

      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i?.exec(hex);
        return result ? [parseInt(result?.[1], 16), parseInt(result?.[2], 16), parseInt(result?.[3], 16)] : [39, 174, 96];
      };

      const brandRgb = hexToRgb(BRAND_GREEN);

      const checkPageBreak = (needed = 10) => {
        if (y + needed > pageHeight - 15) { doc?.addPage(); y = margin; }
      };

      const addSectionHeader = (title) => {
        checkPageBreak(12);
        doc?.setFillColor(...brandRgb);
        doc?.rect(margin, y, contentWidth, 7, 'F');
        doc?.setTextColor(255, 255, 255);
        doc?.setFontSize(9);
        doc?.setFont(undefined, 'bold');
        doc?.text(title, margin + 2, y + 5);
        doc?.setTextColor(0, 0, 0);
        doc?.setFont(undefined, 'normal');
        y += 10;
      };

      const addRow = (label, value, indent = 0) => {
        checkPageBreak(7);
        doc?.setFontSize(8.5);
        doc?.setFont(undefined, 'bold');
        doc?.text(label, margin + indent, y);
        doc?.setFont(undefined, 'normal');
        const valText = String(value || 'N/A');
        const lines = doc?.splitTextToSize(valText, contentWidth - 38 - indent);
        doc?.text(lines, margin + 38 + indent, y);
        y += lines?.length * 4.5 + 1.5;
      };

      // ── Header: white background with logo + project name (matches preview) ──
      let logoDataUrl = null;
      try {
        const logoUrl = await getLogoUrl();
        if (logoUrl) {
          logoDataUrl = await new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              const canvas = document.createElement('canvas');
              canvas.width = img.width; canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);
              resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = () => resolve(null);
            img.src = logoUrl;
          });
        }
      } catch {}

      // White header bar with bottom border
      doc?.setFillColor(255, 255, 255);
      doc?.rect(0, 0, pageWidth, 18, 'F');
      doc?.setDrawColor(226, 232, 240);
      doc?.setLineWidth(0.3);
      doc?.line(0, 18, pageWidth, 18);

      if (logoDataUrl) {
        try { doc?.addImage(logoDataUrl, 'PNG', margin, 2, 12, 12); } catch {}
      }
      doc?.setTextColor(17, 24, 39);
      doc?.setFontSize(13);
      doc?.setFont(undefined, 'bold');
      doc?.text(data?.project?.projectName || 'Project Export', logoDataUrl ? margin + 15 : margin, 10);
      doc?.setTextColor(107, 114, 128);
      doc?.setFontSize(8);
      doc?.setFont(undefined, 'normal');
      doc?.text(
        `View: ${viewOptions?.find((v) => v?.value === viewType)?.label || viewType}  |  Exported: ${new Date()?.toLocaleDateString()}`,
        logoDataUrl ? margin + 15 : margin, 15
      );
      doc?.setTextColor(0, 0, 0);
      y = 24;

      // Overview
      if (includeOverview) {
        addSectionHeader('PROJECT OVERVIEW');
        const p = data?.project;
        if (overviewFields?.projectName) addRow('Project Name:', p?.projectName);
        if (overviewFields?.projectNumber) addRow('Project #:', p?.projectNumber);
        if (overviewFields?.status) addRow('Status:', p?.status);
        if (overviewFields?.customer) addRow('Customer:', p?.customerName);
        if (overviewFields?.startDate) addRow('Start Date:', p?.startDate);
        if (overviewFields?.endDate) addRow('End Date:', p?.endDate);
        if (overviewFields?.budget) addRow('Budget:', p?.totalBudget ? `$${Number(p?.totalBudget)?.toLocaleString()}` : 'N/A');
        if (overviewFields?.location) addRow('Location:', p?.location);
        if (overviewFields?.description && p?.description) addRow('Description:', p?.description);
        y += 4;
      }

      // Phases
      if (includePhases && data?.phases?.length > 0) {
        addSectionHeader('PHASES');
        data?.phases?.forEach((ph, i) => {
          const phInfo = [
            phaseFields?.phaseDates && `${formatDateShort(ph?.startDate)} → ${formatDateShort(ph?.endDate)}`,
            phaseFields?.phaseStatus && ph?.status,
          ]?.filter(Boolean)?.join('  |  ');
          checkPageBreak(phInfo ? 11 : 6);
          // Line 1: phase name (bold)
          doc?.setFontSize(8.5);
          doc?.setFont(undefined, 'bold');
          const nameLabel = `${i + 1}. ${phaseFields?.phaseName !== false ? ph?.name : ''}`;
          doc?.text(nameLabel, margin + 2, y);
          y += 4.5;
          // Line 2: dates / status (smaller, muted)
          if (phInfo) {
            doc?.setFontSize(7.5);
            doc?.setFont(undefined, 'normal');
            doc?.setTextColor(100, 116, 139);
            doc?.text(phInfo, margin + 6, y);
            doc?.setTextColor(0, 0, 0);
            y += 4;
          }
          y += 2;
        });
        y += 4;
      }

      // Gantt
      if (isGantt) {
        const { minDate, totalDays } = computeGanttDateRange();

        // Build active gantt columns for PDF
        const activeGanttCols = [
          ganttColumns?.name !== false && { key: 'name', label: 'Name', w: 40 },
          ganttColumns?.startDate && { key: 'startDate', label: 'Start', w: 18 },
          ganttColumns?.endDate && { key: 'endDate', label: 'End', w: 18 },
          ganttColumns?.status && { key: 'status', label: 'Status', w: 18 },
          ganttColumns?.phase && { key: 'phase', label: 'Phase', w: 22 },
          ganttColumns?.assignedResources && { key: 'resources', label: 'Resources', w: 22 },
          ganttColumns?.duration && { key: 'duration', label: 'Dur.', w: 12 },
          ganttColumns?.percentComplete && { key: 'pct', label: '%', w: 10 },
        ]?.filter(Boolean);

        const labelColW = activeGanttCols?.reduce((s, c) => s + c?.w, 0);
        const barAreaW = contentWidth - labelColW - 4;
        const rowH = 6;
        const barH = 3.5;
        const headerH = 15;

        const MAX_COLS = 32;
        const tickIntervalDays = ganttScale === 'daily' ? 1 : ganttScale === 'monthly' ? 30 : 7;
        const totalTicks = Math.ceil(totalDays / tickIntervalDays);
        const numSections = Math.ceil(totalTicks / MAX_COLS);
        const ticksPerSection = Math.ceil(totalTicks / numSections);
        const daysPerSection = ticksPerSection * tickIntervalDays;

        const phaseRgb = hexToRgb(ganttColors?.phase || '#166534');
        const deliverableRgb = hexToRgb(ganttColors?.deliverable || '#16a34a');
        const taskRgb = hexToRgb(ganttColors?.task || '#fb923c');
        const subtaskRgb = hexToRgb(ganttColors?.subtask || '#a78bfa');

        const getItemColValue = (item, colKey, phaseId) => {
          switch (colKey) {
            case 'name': return item?.name || item?.title || 'Unnamed';
            case 'startDate': return formatDateShort(item?.startDate);
            case 'endDate': return formatDateShort(item?.endDate || item?.dueDate);
            case 'status': return item?.status || '';
            case 'phase': {
              const ph = (phases || [])?.find((p) => p?.id === (item?.phaseId || phaseId));
              return ph?.name || '';
            }
            case 'resources': return (item?.assignedResources || [])?.map((r) => r?.name || r)?.join(', ') || '';
            case 'duration': {
              let s = parseDate(item?.startDate);
              const e = parseDate(item?.endDate || item?.dueDate);
              if (s && e) return `${Math.max(1, Math.round((e - s) / 86400000) + 1)}d`;
              return '';
            }
            case 'pct': return item?.percentComplete != null ? `${item?.percentComplete}%` : '';
            default: return '';
          }
        };

        const drawGanttHeader = (sectionMinDate, sectionDays, sectionIdx) => {
          checkPageBreak(headerH + 4);
          const sectionLabel = numSections > 1
            ? `GANTT CHART — Section ${sectionIdx + 1} of ${numSections}`
            : 'GANTT CHART';
          addSectionHeader(sectionLabel);
          // Column header cells
          let cx = margin;
          activeGanttCols?.forEach((col) => {
            doc?.setFillColor(241, 245, 249);
            doc?.rect(cx, y, col?.w, headerH, 'F');
            doc?.setDrawColor(203, 213, 225);
            doc?.setLineWidth(0.2);
            doc?.rect(cx, y, col?.w, headerH);
            doc?.setFontSize(7);
            doc?.setFont(undefined, 'bold');
            doc?.setTextColor(55, 65, 81);
            doc?.text(col?.label, cx + 1.5, y + headerH / 2 + 1);
            cx += col?.w;
          });
          // Timeline header background — width = barAreaW, aligned to label end
          const tlX = margin + labelColW;
          doc?.setFillColor(241, 245, 249);
          doc?.rect(tlX, y, barAreaW, headerH, 'F');
          doc?.setDrawColor(203, 213, 225);
          doc?.rect(tlX, y, barAreaW, headerH);

          const subH = headerH / 3;
          doc?.setDrawColor(203, 213, 225);
          doc?.setLineWidth(0.15);
          doc?.line(tlX, y + subH, tlX + barAreaW, y + subH);
          doc?.line(tlX, y + subH * 2, tlX + barAreaW, y + subH * 2);

          const yearSpans = {};
          const monthSpans = {};
          for (let d = 0; d < sectionDays; d += tickIntervalDays) {
            const tickDate = new Date(sectionMinDate.getTime() + d * 86400000);
            const yr = tickDate?.getFullYear();
            const mo = `${yr}-${tickDate?.getMonth()}`;
            const xTick = tlX + (d / sectionDays) * barAreaW;
            if (!yearSpans?.[yr]) yearSpans[yr] = { x: xTick, label: String(yr) };
            if (!monthSpans?.[mo]) monthSpans[mo] = { x: xTick, label: tickDate?.toLocaleDateString('en-US', { month: 'short' }) };
          }

          doc?.setFontSize(5);
          doc?.setFont(undefined, 'normal');
          doc?.setTextColor(80, 80, 80);
          for (let d = 0; d < sectionDays; d += tickIntervalDays) {
            const tickDate = new Date(sectionMinDate.getTime() + d * 86400000);
            const xTick = tlX + (d / sectionDays) * barAreaW;
            doc?.setDrawColor(203, 213, 225);
            doc?.setLineWidth(0.15);
            doc?.line(xTick, y + subH * 2, xTick, y + headerH);
            doc?.text(String(tickDate?.getDate())?.padStart(2, '0'), xTick + 0.3, y + subH * 2 + subH * 0.65);
          }

          doc?.setFontSize(6);
          doc?.setFont(undefined, 'bold');
          doc?.setTextColor(30, 41, 59);
          Object.values(yearSpans)?.forEach(({ x, label }) => {
            doc?.text(label, x + 0.5, y + subH * 0.7);
          });

          doc?.setFontSize(5.5);
          doc?.setFont(undefined, 'normal');
          doc?.setTextColor(55, 65, 81);
          Object.values(monthSpans)?.forEach(({ x, label }) => {
            doc?.text(label, x + 0.5, y + subH + subH * 0.65);
          });

          doc?.setTextColor(0, 0, 0);
          doc?.setFont(undefined, 'normal');
          y += headerH + 1;
        };

        const ganttCheckPageBreak = (needed, sectionMinDate, sectionDays, sectionIdx) => {
          if (y + needed > pageHeight - 15) {
            doc?.addPage();
            y = margin;
            drawGanttHeader(sectionMinDate, sectionDays, sectionIdx);
          }
        };

        const renderPdfGanttRows = (rows, currentPhaseId, sectionMinDate, sectionDays, sectionIdx) => {
          rows?.forEach(({ item, indent, phaseId: rowPhaseId }) => {
            const usedPhaseId = rowPhaseId || currentPhaseId;
            ganttCheckPageBreak(rowH + 1, sectionMinDate, sectionDays, sectionIdx);
            const indentMm = indent * 4;
            const tlX = margin + labelColW;

            let cx = margin;
            activeGanttCols?.forEach((col) => {
              if (indent === 0) {
                doc?.setFillColor(248, 250, 252);
                doc?.rect(cx, y, col?.w, rowH, 'F');
              }
              doc?.setDrawColor(226, 232, 240);
              doc?.setLineWidth(0.15);
              doc?.rect(cx, y, col?.w, rowH);

              const cellVal = col?.key === 'name' ? (item?.name || item?.title || 'Unnamed')
                : getItemColValue(item, col?.key, usedPhaseId);

              doc?.setFontSize(col?.key === 'name' ? (indent === 0 ? 8 : 7) : 6.5);
              doc?.setFont(undefined, col?.key === 'name' && indent === 0 ? 'bold' : 'normal');
              doc?.setTextColor(indent === 2 ? 100 : 30, indent === 2 ? 100 : 30, indent === 2 ? 100 : 30);
              const textX = cx + (col?.key === 'name' ? 1.5 + indentMm : 1.5);
              const truncated = doc?.splitTextToSize(cellVal, col?.w - 3 - (col?.key === 'name' ? indentMm : 0))?.[0] || '';
              doc?.text(truncated, textX, y + rowH / 2 + 1.5);
              cx += col?.w;
            });

            doc?.setTextColor(0, 0, 0);
            doc?.setFont(undefined, 'normal');

            // Gantt bar — clipped to this section's date range
            const bp = ganttBarProps(item?.startDate, item?.endDate || item?.dueDate, sectionMinDate, sectionDays, barAreaW);
            if (bp) {
              if (indent === 0) doc?.setFillColor(...deliverableRgb);
              else if (indent === 1) doc?.setFillColor(...taskRgb);
              else doc?.setFillColor(...subtaskRgb);
              doc?.roundedRect(tlX + bp?.x, y + (rowH - barH) / 2, bp?.w, barH, 0.8, 0.8, 'F');
            }
            doc?.setDrawColor(...brandRgb);
            doc?.setLineWidth(0.2);
            // Row separator only to end of bar area (no +2 offset — matches header exactly)
            doc?.line(margin, y + rowH, margin + labelColW + barAreaW, y + rowH);
            y += rowH + 0.5;
          });
        };

        // Render each section
        for (let sectionIdx = 0; sectionIdx < numSections; sectionIdx++) {
          const sectionStartDay = sectionIdx * daysPerSection;
          const sectionEndDay = Math.min(totalDays, sectionStartDay + daysPerSection);
          const sectionDays = sectionEndDay - sectionStartDay;
          const sectionMinDate = new Date(minDate.getTime() + sectionStartDay * 86400000);
          const tlX = margin + labelColW;

          drawGanttHeader(sectionMinDate, sectionDays, sectionIdx);

          const allPhases = data?.phases?.length > 0 ? data?.phases : [null];
          allPhases?.forEach((ph) => {
            if (ph) {
              ganttCheckPageBreak(rowH + 2, sectionMinDate, sectionDays, sectionIdx);
              doc?.setFillColor(209, 250, 229);
              doc?.rect(margin, y, labelColW + barAreaW, rowH, 'F');
              doc?.setFontSize(8);
              doc?.setFont(undefined, 'bold');
              doc?.setTextColor(26, 122, 66);
              doc?.text(ph?.name, margin + 2, y + rowH / 2 + 1.5);
              doc?.setTextColor(0, 0, 0);
              doc?.setFont(undefined, 'normal');
              const bp = ganttBarProps(ph?.startDate, ph?.endDate, sectionMinDate, sectionDays, barAreaW);
              if (bp) {
                doc?.setFillColor(...phaseRgb);
                doc?.roundedRect(tlX + bp?.x, y + 1, bp?.w, barH, 1, 1, 'F');
              }
              y += rowH + 1;
            }

            const phaseId = ph?.id;
            const phaseRows = data?.hierarchy?.filter((r) => phaseId ? (r?.phaseId === phaseId || r?.item?.phaseId === phaseId) : true);
            renderPdfGanttRows(phaseRows, phaseId, sectionMinDate, sectionDays, sectionIdx);
          });

          // Also render items not assigned to any phase (when phases exist)
          if (data?.phases?.length > 0) {
            const phaseIds = new Set(data?.phases?.map((p) => p?.id));
            const unassignedRows = data?.hierarchy?.filter((r) => {
              const pid = r?.phaseId || r?.item?.phaseId;
              return !pid || !phaseIds?.has(pid);
            });
            if (unassignedRows?.length > 0) {
              ganttCheckPageBreak(rowH + 2, sectionMinDate, sectionDays, sectionIdx);
              doc?.setFillColor(241, 245, 249);
              doc?.rect(margin, y, labelColW + barAreaW, rowH, 'F');
              doc?.setFontSize(8);
              doc?.setFont(undefined, 'bold');
              doc?.setTextColor(100, 116, 139);
              doc?.text('Unassigned', margin + 2, y + rowH / 2 + 1.5);
              doc?.setTextColor(0, 0, 0);
              doc?.setFont(undefined, 'normal');
              y += rowH + 1;
              renderPdfGanttRows(unassignedRows, null, sectionMinDate, sectionDays, sectionIdx);
            }
          }

          if (sectionIdx < numSections - 1) {
            doc?.addPage();
            y = margin;
          }
        }

        y += 4;
        checkPageBreak(10);
        doc?.setFontSize(7.5);
        const legendItems = [
          { color: phaseRgb, label: 'Phase' },
          { color: deliverableRgb, label: 'Deliverable' },
          ...(taskDetail === 'all' ? [{ color: taskRgb, label: 'Task' }, { color: subtaskRgb, label: 'Subtask' }] : []),
        ];
        let lx = margin;
        legendItems?.forEach(({ color, label }) => {
          doc?.setFillColor(...color);
          doc?.rect(lx, y, 5, 3, 'F');
          doc?.setTextColor(50, 50, 50);
          doc?.text(label, lx + 6, y + 2.5);
          lx += 22;
        });
        doc?.setTextColor(0, 0, 0);
        y += 8;
      } else {
        addSectionHeader('DELIVERABLES');
        data?.hierarchy?.forEach(({ item, indent }) => {
          checkPageBreak(6.5);
          const indentMm = indent * 6;
          const prefix = indent === 0 ? '▸ ' : indent === 1 ? '  – ' : '    · ';
          doc?.setFontSize(indent === 0 ? 8.5 : 8);
          doc?.setFont(undefined, indent === 0 ? 'bold' : 'normal');
          doc?.setTextColor(indent === 2 ? 100 : 30, indent === 2 ? 100 : 30, indent === 2 ? 100 : 30);
          doc?.text(`${prefix}${item?.name || item?.title || 'Unnamed'}`, margin + indentMm, y);
          doc?.setFont(undefined, 'normal');
          doc?.setTextColor(120, 120, 120);
          doc?.setFontSize(7.5);
          const details = [
            deliverableFields?.status && `Status: ${item?.status || 'N/A'}`,
            deliverableFields?.dueDate && `Due: ${formatDateShort(item?.dueDate || item?.endDate)}`,
            deliverableFields?.startDate && `Start: ${formatDateShort(item?.startDate)}`,
          ]?.filter(Boolean)?.join('  |  ');
          doc?.text(details, margin + indentMm + 2, y + 4);
          doc?.setDrawColor(...brandRgb);
          doc?.setLineWidth(0.2);
          doc?.line(margin, y + 7, margin + contentWidth, y + 7);
          doc?.setDrawColor(226, 232, 240);
          doc?.setTextColor(0, 0, 0);
          y += 9;
        });
      }

      const projectName = data?.project?.projectName?.replace(/[^a-z0-9]/gi, '_') || 'project';
      doc?.save(`${projectName}_export.pdf`);
    } catch (err) {
      console.error('PDF export error:', err);
      throw new Error('Failed to generate PDF. Please try again.');
    }
  };

  // ── Excel Export ───────────────────────────────────────────────────────────
  const exportAsExcel = async (data) => {
    try {
      const XLSX = await import('xlsx');
      const { minDate, maxDate, totalDays } = computeGanttDateRange();
      const timelineCols = [];

      if (ganttScale === 'daily') {
        for (let d = 0; d <= totalDays; d++) {
          const dt = new Date(minDate.getTime() + d * 86400000);
          timelineCols?.push({ label: dt?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), startDay: d, endDay: d });
        }
      } else if (ganttScale === 'weekly') {
        const weekStart = new Date(minDate);
        const dow = weekStart?.getDay();
        weekStart?.setDate(weekStart?.getDate() - ((dow + 6) % 7));
        let cursor = new Date(weekStart);
        while (cursor <= maxDate) {
          const startDay = Math.round((cursor - minDate) / 86400000);
          const endDay = startDay + 6;
          cursor?.setDate(cursor?.getDate() + 7);
          timelineCols?.push({ label: cursor?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), startDay, endDay });
        }
      } else {
        let cursor = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
        while (cursor <= maxDate) {
          const startDay = Math.max(0, Math.round((cursor - minDate) / 86400000));
          const nextMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
          const endDay = Math.round((nextMonth - minDate) / 86400000) - 1;
          timelineCols?.push({ label: cursor?.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }), startDay, endDay });
          cursor = nextMonth;
        }
      }

      const FIXED_HEADERS = ['Type', 'Name', 'Status', 'Start Date', 'Due Date', 'Phase'];
      const aoa = [];

      if (includeOverview) {
        const p = data?.project;
        aoa?.push(['PROJECT OVERVIEW']);
        aoa?.push(['Field', 'Value']);
        if (overviewFields?.projectName) aoa?.push(['Project Name', p?.projectName || '']);
        if (overviewFields?.projectNumber) aoa?.push(['Project Number', p?.projectNumber || '']);
        if (overviewFields?.status) aoa?.push(['Status', p?.status || '']);
        if (overviewFields?.customer) aoa?.push(['Customer', p?.customerName || '']);
        if (overviewFields?.startDate) aoa?.push(['Start Date', p?.startDate || '']);
        if (overviewFields?.endDate) aoa?.push(['End Date', p?.endDate || '']);
        if (overviewFields?.budget) aoa?.push(['Total Budget', p?.totalBudget ? `$${Number(p?.totalBudget)?.toLocaleString()}` : '']);
        if (overviewFields?.location) aoa?.push(['Location', p?.location || '']);
        if (overviewFields?.description) aoa?.push(['Description', p?.description || '']);
        aoa?.push([]);
      }

      if (includePhases && data?.phases?.length > 0) {
        aoa?.push(['PHASES']);
        const phaseHeader = [];
        if (phaseFields?.phaseName) phaseHeader?.push('Phase Name');
        if (phaseFields?.phaseDates) phaseHeader?.push('Start Date', 'End Date');
        if (phaseFields?.phaseStatus) phaseHeader?.push('Status');
        aoa?.push(phaseHeader);
        data?.phases?.forEach((ph) => {
          const row = [];
          if (phaseFields?.phaseName) row?.push(ph?.name);
          if (phaseFields?.phaseDates) row?.push(ph?.startDate, ph?.endDate);
          if (phaseFields?.phaseStatus) row?.push(ph?.status);
          aoa?.push(row);
        });
        aoa?.push([]);
      }

      aoa?.push(['GANTT CHART', '', '', '', '', '', ...timelineCols?.map((c) => c?.label)]);
      aoa?.push([...FIXED_HEADERS, ...timelineCols?.map((c) => c?.label)]);

      const getBarCols = (startStr, endStr) => {
        let s = parseDate(startStr);
        const e = parseDate(endStr);
        if (!s || !e) return new Set();
        const itemStartDay = Math.round((s - minDate) / 86400000);
        const itemEndDay = Math.round((e - minDate) / 86400000);
        const covered = new Set();
        timelineCols?.forEach((col, idx) => {
          if (itemStartDay <= col?.endDay && itemEndDay >= col?.startDay) covered?.add(idx);
        });
        return covered;
      };

      const allPhases = data?.phases?.length > 0 ? data?.phases : [null];
      allPhases?.forEach((ph) => {
        if (ph) {
          const barCols = getBarCols(ph?.startDate, ph?.endDate);
          const ganttCells = timelineCols?.map((_, idx) => (barCols?.has(idx) ? '█' : ''));
          aoa?.push(['Phase', ph?.name, ph?.status || '', ph?.startDate || '', ph?.endDate || '', '', ...ganttCells]);
        }
        const phaseId = ph?.id;
        const phaseRows = data?.hierarchy?.filter((r) => phaseId ? (r?.phaseId === phaseId || r?.item?.phaseId === phaseId) : true);
        phaseRows?.forEach(({ item, indent }) => {
          const prefix = indent === 0 ? '' : indent === 1 ? '    ' : '        ';
          const typeLabel = indent === 0 ? 'Deliverable' : indent === 1 ? 'Task' : 'Subtask';
          const barCols = getBarCols(item?.startDate, item?.endDate || item?.dueDate);
          const ganttCells = timelineCols?.map((_, idx) => (barCols?.has(idx) ? '█' : ''));
          aoa?.push([
            typeLabel,
            prefix + (item?.name || item?.title || 'Unnamed'),
            deliverableFields?.status ? (item?.status || '') : '',
            deliverableFields?.startDate ? (item?.startDate || '') : '',
            deliverableFields?.dueDate ? (item?.dueDate || item?.endDate || '') : '',
            item?.phaseName || ph?.name || '',
            ...ganttCells,
          ]);
        });
      });

      aoa?.push([]);
      aoa?.push(['LEGEND: Phase=█ (blue)  |  Deliverable=█ (green)  |  Task=█ (orange)  |  Subtask=█ (purple)']);

      const ws = XLSX?.utils?.aoa_to_sheet(aoa);
      const colWidths = [{ wch: 14 }, { wch: 36 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 20 }];
      const timelineColWidth = ganttScale === 'daily' ? 5 : ganttScale === 'weekly' ? 8 : 10;
      timelineCols?.forEach(() => colWidths?.push({ wch: timelineColWidth }));
      ws['!cols'] = colWidths;
      XLSX?.utils?.book_append_sheet(XLSX?.utils?.book_new(), ws, 'Project Export');
      const wb = XLSX?.utils?.book_new();
      XLSX?.utils?.book_append_sheet(wb, ws, 'Project Export');
      const projectName = data?.project?.projectName?.replace(/[^a-z0-9]/gi, '_') || 'project';
      XLSX?.writeFile(wb, `${projectName}_export.xlsx`);
    } catch (err) {
      console.error('Excel export error:', err);
      throw new Error('Failed to generate Excel file. Please try again.');
    }
  };

  // ── Image Export ───────────────────────────────────────────────────────────
  const exportAsImage = async (data) => {
    try {
      const isGantt = viewType === 'gantt' || viewType === 'gantt-resources';
      const { minDate, totalDays } = computeGanttDateRange();
      const padding = 28;
      // Compute dynamic label column width based on active gantt columns
      const imgActiveGanttCols = isGantt ? [
        ganttColumns?.name !== false && { key: 'name', label: 'Name', w: 150 },
        ganttColumns?.startDate && { key: 'startDate', label: 'Start', w: 72 },
        ganttColumns?.endDate && { key: 'endDate', label: 'End', w: 72 },
        ganttColumns?.status && { key: 'status', label: 'Status', w: 72 },
        ganttColumns?.phase && { key: 'phase', label: 'Phase', w: 80 },
        ganttColumns?.assignedResources && { key: 'resources', label: 'Resources', w: 80 },
        ganttColumns?.duration && { key: 'duration', label: 'Dur.', w: 55 },
        ganttColumns?.percentComplete && { key: 'pct', label: '% Done', w: 55 },
      ]?.filter(Boolean) : [];

      const labelColW = isGantt ? imgActiveGanttCols?.reduce((s, c) => s + c?.w, 0) : 260;
      const barAreaW = 700;
      const canvasWidth = padding * 2 + labelColW + barAreaW;
      const rowH = 22;
      const barH = 12;
      const headerSectionH = 70;
      const ganttHeaderH = 54;

      const MAX_COLS = 32;
      const tickIntervalDays = ganttScale === 'daily' ? 1 : ganttScale === 'monthly' ? 30 : 7;
      const totalTicks = Math.ceil(totalDays / tickIntervalDays);
      const numSections = isGantt ? Math.ceil(totalTicks / MAX_COLS) : 1;
      const ticksPerSection = Math.ceil(totalTicks / numSections);
      const daysPerSection = ticksPerSection * tickIntervalDays;

      const phaseRows = data?.phases?.length || 0;
      const itemRows = data?.hierarchy?.length;
      const totalRows = phaseRows + itemRows;
      let overviewH = includeOverview ? 160 : 0;
      // Two-line phase rows: ~36px each (name + info line) + section header
      let phasesH = includePhases && data?.phases?.length > 0 ? data?.phases?.length * 36 + 40 : 0;
      const ganttSectionH = isGantt ? (ganttHeaderH + totalRows * (rowH + 2) + 30) : 0;
      const ganttH = isGantt ? ganttSectionH * numSections + 60 : 0;
      const listH = !isGantt ? itemRows * 26 + 40 : 0;
      const canvasHeight = headerSectionH + overviewH + phasesH + ganttH + listH + padding * 2;

      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas?.getContext('2d');

      ctx.fillStyle = '#f8fafc';
      ctx?.fillRect(0, 0, canvasWidth, canvasHeight);
      ctx.fillStyle = '#ffffff';
      ctx?.fillRect(0, 0, canvasWidth, headerSectionH);

      try {
        const logoUrl = await getLogoUrl();
        if (logoUrl) {
          await new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              const logoH = 44;
              const logoW = (img.width / img.height) * logoH;
              ctx.drawImage(img, padding, (headerSectionH - logoH) / 2, logoW, logoH);
              resolve();
            };
            img.onerror = resolve;
            img.src = logoUrl;
          });
        }
      } catch {}

      ctx.fillStyle = '#111827';
      ctx.font = 'bold 20px Arial, sans-serif';
      ctx?.fillText(data?.project?.projectName || 'Project Export', padding + 60, 36);
      ctx.font = '13px Arial, sans-serif';
      ctx.fillStyle = '#6b7280';
      ctx?.fillText(`View: ${viewOptions?.find((v) => v?.value === viewType)?.label || viewType}  |  Exported: ${new Date()?.toLocaleDateString()}`, padding + 60, 56);

      let cy = headerSectionH + padding;

      const drawSectionHeader = (title) => {
        ctx.fillStyle = BRAND_GREEN;
        ctx?.fillRect(padding, cy, canvasWidth - padding * 2, 24);
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 13px Arial, sans-serif';
        ctx?.fillText(title, padding + 8, cy + 16);
        cy += 28;
      };

      if (includeOverview) {
        drawSectionHeader('PROJECT OVERVIEW');
        const p = data?.project;
        const fields = [
          overviewFields?.projectName && ['Project Name', p?.projectName],
          overviewFields?.projectNumber && ['Project #', p?.projectNumber],
          overviewFields?.status && ['Status', p?.status],
          overviewFields?.customer && ['Customer', p?.customerName],
          overviewFields?.startDate && ['Start Date', p?.startDate],
          overviewFields?.endDate && ['End Date', p?.endDate],
          overviewFields?.budget && ['Budget', p?.totalBudget ? `$${Number(p?.totalBudget)?.toLocaleString()}` : 'N/A'],
          overviewFields?.location && ['Location', p?.location],
          overviewFields?.description && ['Description', p?.description],
        ]?.filter(Boolean);
        fields?.forEach(([label, val]) => {
          ctx.font = 'bold 12px Arial, sans-serif';
          ctx.fillStyle = '#374151';
          ctx?.fillText(`${label}:`, padding + 4, cy + 14);
          ctx.font = '12px Arial, sans-serif';
          ctx.fillStyle = '#1e293b';
          ctx?.fillText(String(val || 'N/A'), padding + 110, cy + 14);
          ctx.strokeStyle = BRAND_GREEN;
          ctx.lineWidth = 0.5;
          ctx?.beginPath();
          ctx?.moveTo(padding, cy + 18);
          ctx?.lineTo(canvasWidth - padding, cy + 18);
          ctx?.stroke();
          cy += 18;
        });
        cy += 10;
      }

      if (includePhases && data?.phases?.length > 0) {
        drawSectionHeader('PHASES');
        data?.phases?.forEach((ph, i) => {
          // Line 1: phase name (bold)
          ctx.font = 'bold 12px Arial, sans-serif';
          ctx.fillStyle = BRAND_GREEN_DARK;
          ctx?.fillText(`${i + 1}. ${ph?.name}`, padding + 4, cy + 14);
          // Line 2: dates / status (smaller, muted) — only if there's info to show
          const phInfo = [
            phaseFields?.phaseDates && `${formatDateShort(ph?.startDate)} → ${formatDateShort(ph?.endDate)}`,
            phaseFields?.phaseStatus && ph?.status,
          ]?.filter(Boolean)?.join('  |  ');
          if (phInfo) {
            ctx.font = '11px Arial, sans-serif';
            ctx.fillStyle = '#64748b';
            ctx?.fillText(phInfo, padding + 14, cy + 24);
          }
          ctx.strokeStyle = BRAND_GREEN;
          ctx.lineWidth = 0.5;
          ctx?.beginPath();
          ctx?.moveTo(padding, cy + (phInfo ? 28 : 18));
          ctx?.lineTo(canvasWidth - padding, cy + (phInfo ? 28 : 18));
          ctx?.stroke();
          cy += phInfo ? 32 : 20;
        });
        cy += 10;
      }

      if (isGantt) {
        const ganttLeft = padding + labelColW;
        // ganttW is fixed to barAreaW — content must not exceed this
        const ganttW = barAreaW;
        // Total content width for this gantt section (label cols + bar area)
        const ganttContentW = labelColW + ganttW;

        const getImgItemColValue = (item, colKey, phaseId) => {
          switch (colKey) {
            case 'name': return item?.name || item?.title || 'Unnamed';
            case 'startDate': return formatDateShort(item?.startDate);
            case 'endDate': return formatDateShort(item?.endDate || item?.dueDate);
            case 'status': return item?.status || '';
            case 'phase': {
              const ph = (phases || [])?.find((p) => p?.id === (item?.phaseId || phaseId));
              return ph?.name || '';
            }
            case 'resources': return (item?.assignedResources || [])?.map((r) => r?.name || r)?.join(', ') || '';
            case 'duration': {
              let s = parseDate(item?.startDate);
              const e = parseDate(item?.endDate || item?.dueDate);
              if (s && e) return `${Math.max(1, Math.round((e - s) / 86400000) + 1)}d`;
              return '';
            }
            case 'pct': return item?.percentComplete != null ? `${item?.percentComplete}%` : '';
            default: return '';
          }
        };

        const renderGanttSection = (sectionIdx) => {
          const sectionStartDay = sectionIdx * daysPerSection;
          const sectionEndDay = Math.min(totalDays, sectionStartDay + daysPerSection);
          const sectionDays = sectionEndDay - sectionStartDay;
          const sectionMinDate = new Date(minDate.getTime() + sectionStartDay * 86400000);

          const secLabel = numSections > 1
            ? `GANTT CHART — Section ${sectionIdx + 1} of ${numSections}`
            : 'GANTT CHART';
          drawSectionHeader(secLabel);

          // Draw column header row
          let hColX = padding;
          imgActiveGanttCols?.forEach((col) => {
            ctx.fillStyle = '#f1f5f9';
            ctx?.fillRect(hColX, cy, col?.w, ganttHeaderH);
            ctx.strokeStyle = '#cbd5e1';
            ctx.lineWidth = 0.5;
            ctx?.strokeRect(hColX, cy, col?.w, ganttHeaderH);
            ctx.font = 'bold 10px Arial, sans-serif';
            ctx.fillStyle = '#374151';
            ctx?.fillText(col?.label, hColX + 5, cy + ganttHeaderH / 2 + 4);
            hColX += col?.w;
          });

          // Timeline header — fixed width = ganttW
          ctx.fillStyle = '#e2e8f0';
          ctx?.fillRect(ganttLeft, cy, ganttW, ganttHeaderH);
          ctx.strokeStyle = BRAND_GREEN;
          ctx.lineWidth = 1;
          ctx?.strokeRect(ganttLeft, cy, ganttW, ganttHeaderH);

          const imgSubRowH = ganttHeaderH / 3;
          [1, 2]?.forEach((i) => {
            ctx.strokeStyle = '#cbd5e1';
            ctx.lineWidth = 0.5;
            ctx?.beginPath();
            ctx?.moveTo(ganttLeft, cy + imgSubRowH * i);
            ctx?.lineTo(ganttLeft + ganttW, cy + imgSubRowH * i);
            ctx?.stroke();
          });

          const imgYearSpans = {};
          const imgMonthSpans = {};
          for (let d = 0; d < sectionDays; d += tickIntervalDays) {
            const tickDate = new Date(sectionMinDate.getTime() + d * 86400000);
            const yr = tickDate?.getFullYear();
            const mo = `${yr}-${tickDate?.getMonth()}`;
            const xTick = ganttLeft + (d / sectionDays) * ganttW;
            if (!imgYearSpans?.[yr]) imgYearSpans[yr] = { x: xTick, label: String(yr) };
            if (!imgMonthSpans?.[mo]) imgMonthSpans[mo] = { x: xTick, label: tickDate?.toLocaleDateString('en-US', { month: 'short' }) };
          }

          for (let d = 0; d < sectionDays; d += tickIntervalDays) {
            const tickDate = new Date(sectionMinDate.getTime() + d * 86400000);
            const xTick = ganttLeft + (d / sectionDays) * ganttW;
            ctx.strokeStyle = BRAND_GREEN;
            ctx.lineWidth = 0.3;
            ctx?.beginPath();
            ctx?.moveTo(xTick, cy + imgSubRowH * 2);
            ctx?.lineTo(xTick, cy + ganttHeaderH);
            ctx?.stroke();
            ctx.font = '9px Arial, sans-serif';
            ctx.fillStyle = '#475569';
            ctx?.fillText(String(tickDate?.getDate())?.padStart(2, '0'), xTick + 2, cy + imgSubRowH * 2 + imgSubRowH * 0.65);
          }

          ctx.font = 'bold 11px Arial, sans-serif';
          ctx.fillStyle = '#1e293b';
          Object.values(imgYearSpans)?.forEach(({ x, label }) => {
            ctx?.fillText(label, x + 3, cy + imgSubRowH * 0.7);
          });

          ctx.font = '9px Arial, sans-serif';
          ctx.fillStyle = '#374151';
          Object.values(imgMonthSpans)?.forEach(({ x, label }) => {
            ctx?.fillText(label, x + 3, cy + imgSubRowH + imgSubRowH * 0.65);
          });

          cy += ganttHeaderH;

          // Clip the entire gantt rows area to prevent any bleed past the header right edge
          ctx?.save();
          ctx?.beginPath();
          ctx?.rect(padding, cy, ganttContentW, canvasHeight);
          ctx?.clip();

          const renderImageGanttRows = (rows, currentPhaseId) => {
            rows?.forEach(({ item, indent, phaseId: rowPhaseId }) => {
              const usedPhaseId = rowPhaseId || currentPhaseId;
              const indentPx = indent * 14;
              ctx.fillStyle = indent % 2 === 0 ? '#f8fafc' : '#f1f5f9';
              // Row background only covers label + bar area (no bleed past right edge)
              ctx?.fillRect(padding, cy, ganttContentW, rowH);

              let cx2 = padding;
              imgActiveGanttCols?.forEach((col) => {
                const cellVal = getImgItemColValue(item, col?.key, usedPhaseId);
                ctx.font = (col?.key === 'name' && indent === 0) ? 'bold 11px Arial, sans-serif' : '10px Arial, sans-serif';
                ctx.fillStyle = indent === 2 ? '#6b7280' : '#1e293b';
                const textX = cx2 + (col?.key === 'name' ? 5 + indentPx : 5);
                ctx?.save();
                ctx?.beginPath();
                ctx?.rect(cx2, cy, col?.w - 3, rowH);
                ctx?.clip();
                ctx?.fillText(cellVal, textX, cy + rowH / 2 + 4);
                ctx?.restore();
                ctx.strokeStyle = '#e2e8f0';
                ctx.lineWidth = 0.4;
                ctx?.beginPath();
                ctx?.moveTo(cx2 + col?.w, cy);
                ctx?.lineTo(cx2 + col?.w, cy + rowH);
                ctx?.stroke();
                cx2 += col?.w;
              });

              // Gantt bar — clipped to this section's date range
              const bp = ganttBarProps(item?.startDate, item?.endDate || item?.dueDate, sectionMinDate, sectionDays, ganttW);
              if (bp) {
                ctx.fillStyle = indent === 0 ? (ganttColors?.deliverable || BRAND_GREEN) : indent === 1 ? (ganttColors?.task || '#fb923c') : (ganttColors?.subtask || '#a78bfa');
                ctx?.save();
                ctx?.beginPath();
                ctx?.rect(ganttLeft, cy, ganttW, rowH);
                ctx?.clip();
                ctx?.beginPath();
                ctx?.roundRect(ganttLeft + bp?.x, cy + (rowH - barH) / 2, bp?.w, barH, 3);
                ctx?.fill();
                ctx?.restore();
              }
              ctx.strokeStyle = BRAND_GREEN;
              ctx.lineWidth = 0.4;
              ctx?.beginPath();
              ctx?.moveTo(padding, cy + rowH);
              ctx?.lineTo(padding + ganttContentW, cy + rowH);
              ctx?.stroke();
              cy += rowH + 2;
            });
          };

          if (data?.phases?.length > 0) {
            data?.phases?.forEach((ph) => {
              ctx.fillStyle = BRAND_GREEN_LIGHT;
              ctx?.fillRect(padding, cy, ganttContentW, rowH);
              ctx.font = 'bold 12px Arial, sans-serif';
              ctx.fillStyle = BRAND_GREEN_DARK;
              ctx?.fillText(ph?.name, padding + 6, cy + rowH / 2 + 4);
              const bp = ganttBarProps(ph?.startDate, ph?.endDate, sectionMinDate, sectionDays, ganttW);
              if (bp) {
                ctx.fillStyle = ganttColors?.phase || BRAND_GREEN;
                ctx?.save();
                ctx?.beginPath();
                ctx?.rect(ganttLeft, cy, ganttW, rowH);
                ctx?.clip();
                ctx?.beginPath();
                ctx?.roundRect(ganttLeft + bp?.x, cy + (rowH - barH) / 2, bp?.w, barH, 3);
                ctx?.fill();
                ctx?.restore();
              }
              ctx.strokeStyle = BRAND_GREEN;
              ctx.lineWidth = 0.8;
              ctx?.beginPath();
              ctx?.moveTo(padding, cy + rowH);
              ctx?.lineTo(padding + ganttContentW, cy + rowH);
              ctx?.stroke();
              cy += rowH + 2;

              const phaseItems = data?.hierarchy?.filter((r) => r?.phaseId === ph?.id || r?.item?.phaseId === ph?.id);
              renderImageGanttRows(phaseItems, ph?.id);
            });
            // Render items not assigned to any phase
            const imgPhaseIds = new Set(data?.phases?.map((p) => p?.id));
            const imgUnassignedRows = data?.hierarchy?.filter((r) => {
              const pid = r?.phaseId || r?.item?.phaseId;
              return !pid || !imgPhaseIds?.has(pid);
            });
            if (imgUnassignedRows?.length > 0) {
              ctx.fillStyle = '#f1f5f9';
              ctx?.fillRect(padding, cy, ganttContentW, rowH);
              ctx.font = 'bold 12px Arial, sans-serif';
              ctx.fillStyle = '#64748b';
              ctx?.fillText('Unassigned', padding + 6, cy + rowH / 2 + 4);
              cy += rowH + 2;
              renderImageGanttRows(imgUnassignedRows, null);
            }
          } else {
            renderImageGanttRows(data?.hierarchy, null);
          }

          // Restore clip after all rows
          ctx?.restore();

          cy += 10;
        };

        for (let s = 0; s < numSections; s++) {
          renderGanttSection(s);
        }

        const legendItems = [
          { color: ganttColors?.phase || '#166534', label: 'Phase' },
          { color: ganttColors?.deliverable || '#16a34a', label: 'Deliverable' },
          ...(taskDetail === 'all' ? [{ color: ganttColors?.task || '#fb923c', label: 'Task' }, { color: ganttColors?.subtask || '#a78bfa', label: 'Subtask' }] : []),
        ];
        let lx = padding;
        legendItems?.forEach(({ color, label }) => {
          ctx.fillStyle = color;
          ctx?.fillRect(lx, cy, 16, 10);
          ctx.font = '11px Arial, sans-serif';
          ctx.fillStyle = '#374151';
          ctx?.fillText(label, lx + 20, cy + 9);
          lx += 90;
        });
        cy += 20;
      } else {
        drawSectionHeader('DELIVERABLES & TASKS');
        data?.hierarchy?.forEach(({ item, indent }) => {
          const indentPx = indent * 20;
          const prefix = indent === 0 ? '▸ ' : indent === 1 ? '– ' : '· ';
          ctx.font = indent === 0 ? 'bold 12px Arial, sans-serif' : '12px Arial, sans-serif';
          ctx.fillStyle = indent === 2 ? '#6b7280' : '#1e293b';
          ctx?.fillText(`${prefix}${item?.name || item?.title || 'Unnamed'}`, padding + 4 + indentPx, cy + 14);
          const details = [
            deliverableFields?.status && `Status: ${item?.status || 'N/A'}`,
            deliverableFields?.dueDate && `Due: ${formatDateShort(item?.dueDate || item?.endDate)}`,
          ]?.filter(Boolean)?.join('  |  ');
          ctx.font = '10px Arial, sans-serif';
          ctx.fillStyle = '#94a3b8';
          ctx?.fillText(details, padding + 6 + indentPx, cy + 24);
          cy += 28;
        });
      }

      const link = document.createElement('a');
      const projectName = data?.project?.projectName?.replace(/[^a-z0-9]/gi, '_') || 'project';
      link.download = `${projectName}_export.png`;
      link.href = canvas?.toDataURL('image/png');
      link?.click();
    } catch (err) {
      console.error('Image export error:', err);
      throw new Error('Failed to generate image. Please try again.');
    }
  };

  // ── Handle Export ──────────────────────────────────────────────────────────
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const data = buildExportData();
      if (exportFormat === 'excel') await exportAsExcel(data);
      else if (exportFormat === 'pdf') await exportAsPDF(data);
      else if (exportFormat === 'image') await exportAsImage(data);
      onClose();
    } catch (err) {
      console.error('Export failed:', err);
      alert(err?.message || 'Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // ── Sub-checkbox helper ────────────────────────────────────────────────────
  const SubCheckbox = ({ checked, onChange, label }) => (
    <label className="flex items-center gap-2 cursor-pointer group">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e?.target?.checked)}
        className="w-3.5 h-3.5 rounded border-border text-primary accent-primary"
      />
      <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">{label}</span>
    </label>
  );

  // ── Color swatch picker helper ─────────────────────────────────────────────
  const ColorPickerRow = ({ label, colorKey, previewColor }) => (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <div className="w-4 h-4 rounded-sm flex-shrink-0" style={{ backgroundColor: previewColor }} />
        <span className="text-xs text-foreground">{label}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <input
          type="color"
          value={previewColor}
          onChange={(e) => updateNestedField('ganttColors', colorKey, e?.target?.value)}
          className="w-7 h-7 rounded cursor-pointer border border-border p-0.5 bg-transparent"
          title={`Pick color for ${label}`}
        />
        <span className="text-xs text-muted-foreground font-mono w-16">{previewColor}</span>
      </div>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-20">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0 bg-white rounded-t-xl">
          <div className="flex items-center gap-2">
            <Icon name="Download" size={20} className="text-foreground" />
            <h2 className="text-lg font-semibold text-foreground">Export Project</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (!showPreview) generatePreview();
                setShowPreview((v) => !v);
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all border ${
                showPreview
                  ? 'bg-primary/10 border-primary text-primary' :'border-border text-muted-foreground hover:text-foreground hover:border-primary/40'
              }`}
            >
              <Icon name="Eye" size={15} />
              {showPreview ? 'Hide Preview' : 'Preview'}
            </button>
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-muted transition-colors">
              <Icon name="X" size={18} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Body — two-column when preview is open */}
        <div className={`flex flex-1 overflow-hidden ${showPreview ? 'divide-x divide-border' : ''}`}>
          {/* Settings panel */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

            {/* ── Report Presets ── */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Bookmark" size={15} className="text-primary" />
                  Report Presets
                </h3>
                <button
                  onClick={() => setShowSavePreset((v) => !v)}
                  className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors font-medium"
                >
                  <Icon name="Plus" size={13} />
                  Save Current as Preset
                </button>
              </div>

              {/* Save preset input */}
              {showSavePreset && (
                <div className="mb-3 flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                  <input
                    type="text"
                    value={presetName}
                    onChange={(e) => setPresetName(e?.target?.value)}
                    onKeyDown={(e) => { if (e?.key === 'Enter') handleSavePreset(); if (e?.key === 'Escape') { setShowSavePreset(false); setPresetName(''); } }}
                    placeholder="e.g. Weekly Status, Budget Review…"
                    className="flex-1 text-sm bg-transparent border-none outline-none text-foreground placeholder:text-muted-foreground"
                    autoFocus
                  />
                  <button
                    onClick={handleSavePreset}
                    disabled={!presetName?.trim() || isSavingPreset}
                    className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
                  >
                    {isSavingPreset ? (
                      <span className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Icon name="Check" size={12} />
                    )}
                    Save
                  </button>
                  <button
                    onClick={() => { setShowSavePreset(false); setPresetName(''); }}
                    className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon name="X" size={13} />
                  </button>
                </div>
              )}

              {/* Preset list */}
              {presetsLoaded && presets?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {presets?.map((preset) => (
                    <div
                      key={preset?.id}
                      className="group flex items-center gap-1 pl-3 pr-1 py-1.5 rounded-full border border-border bg-muted/40 hover:border-primary/40 hover:bg-primary/5 transition-all"
                    >
                      <button
                        onClick={() => applyPreset(preset)}
                        className="text-xs font-medium text-foreground group-hover:text-primary transition-colors"
                        title={`Apply preset: ${preset?.name}`}
                      >
                        {preset?.name}
                      </button>
                      <button
                        onClick={() => handleDeletePreset(preset?.id)}
                        className="ml-1 p-0.5 rounded-full text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                        title="Delete preset"
                      >
                        <Icon name="X" size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : presetsLoaded ? (
                <p className="text-xs text-muted-foreground italic">
                  No presets yet. Configure your settings and click "Save Current as Preset" to create one.
                </p>
              ) : (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="w-3 h-3 border border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
                  Loading presets…
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="border-t border-border" />

            {/* Step 1: Export Format */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">1</span>
                Export Format
              </h3>
              <div className="grid grid-cols-3 gap-3">
                {formatOptions?.map((opt) => (
                  <button
                    key={opt?.value}
                    onClick={() => updateSettings({ exportFormat: opt?.value })}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                      exportFormat === opt?.value
                        ? 'border-primary bg-primary/5 text-primary' :'border-border hover:border-primary/40 text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name={opt?.icon} size={20} />
                    <span className="text-sm font-medium">{opt?.label}</span>
                    <span className="text-xs text-center opacity-70">{opt?.description}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Data to Include */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">2</span>
                Data to Include
              </h3>
              <div className="space-y-2">
                {/* Project Overview */}
                <div className={`rounded-lg border transition-colors ${includeOverview ? 'border-primary/30 bg-primary/5' : 'border-border'}`}>
                  <label className="flex items-center gap-3 p-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeOverview}
                      onChange={(e) => updateSettings({ includeOverview: e?.target?.checked })}
                      className="w-4 h-4 rounded border-border text-primary accent-primary"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">Project Overview</p>
                      <p className="text-xs text-muted-foreground">Name, status, dates, budget, customer, location</p>
                    </div>
                  </label>
                  {includeOverview && (
                    <div className="px-4 pb-3 grid grid-cols-3 gap-x-4 gap-y-1.5 border-t border-primary/10 pt-2">
                      <SubCheckbox checked={overviewFields?.projectName} onChange={(v) => updateNestedField('overviewFields', 'projectName', v)} label="Project Name" />
                      <SubCheckbox checked={overviewFields?.projectNumber} onChange={(v) => updateNestedField('overviewFields', 'projectNumber', v)} label="Project #" />
                      <SubCheckbox checked={overviewFields?.status} onChange={(v) => updateNestedField('overviewFields', 'status', v)} label="Status" />
                      <SubCheckbox checked={overviewFields?.customer} onChange={(v) => updateNestedField('overviewFields', 'customer', v)} label="Customer" />
                      <SubCheckbox checked={overviewFields?.startDate} onChange={(v) => updateNestedField('overviewFields', 'startDate', v)} label="Start Date" />
                      <SubCheckbox checked={overviewFields?.endDate} onChange={(v) => updateNestedField('overviewFields', 'endDate', v)} label="End Date" />
                      <SubCheckbox checked={overviewFields?.budget} onChange={(v) => updateNestedField('overviewFields', 'budget', v)} label="Budget" />
                      <SubCheckbox checked={overviewFields?.location} onChange={(v) => updateNestedField('overviewFields', 'location', v)} label="Location" />
                      <SubCheckbox checked={overviewFields?.description} onChange={(v) => updateNestedField('overviewFields', 'description', v)} label="Description" />
                    </div>
                  )}
                </div>

                {/* Phase Information */}
                <div className={`rounded-lg border transition-colors ${includePhases ? 'border-primary/30 bg-primary/5' : 'border-border'}`}>
                  <label className="flex items-center gap-3 p-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includePhases}
                      onChange={(e) => updateSettings({ includePhases: e?.target?.checked })}
                      className="w-4 h-4 rounded border-border text-primary accent-primary"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">Phase Information</p>
                      <p className="text-xs text-muted-foreground">
                        All {phases?.length || 0} phase{phases?.length !== 1 ? 's' : ''} with dates and status
                      </p>
                    </div>
                  </label>
                  {includePhases && (
                    <div className="px-4 pb-3 flex gap-4 border-t border-primary/10 pt-2">
                      <SubCheckbox checked={phaseFields?.phaseName} onChange={(v) => updateNestedField('phaseFields', 'phaseName', v)} label="Phase Name" />
                      <SubCheckbox checked={phaseFields?.phaseDates} onChange={(v) => updateNestedField('phaseFields', 'phaseDates', v)} label="Dates" />
                      <SubCheckbox checked={phaseFields?.phaseStatus} onChange={(v) => updateNestedField('phaseFields', 'phaseStatus', v)} label="Status" />
                    </div>
                  )}
                </div>

                {/* Deliverable Fields */}
                <div className="rounded-lg border border-border">
                  <div className="p-3">
                    <p className="text-sm font-medium text-foreground">Deliverable Fields</p>
                    <p className="text-xs text-muted-foreground mb-2">Choose which columns to include for deliverables</p>
                    <div className="grid grid-cols-3 gap-x-4 gap-y-1.5">
                      <SubCheckbox checked={deliverableFields?.name} onChange={(v) => updateNestedField('deliverableFields', 'name', v)} label="Name" />
                      <SubCheckbox checked={deliverableFields?.status} onChange={(v) => updateNestedField('deliverableFields', 'status', v)} label="Status" />
                      <SubCheckbox checked={deliverableFields?.startDate} onChange={(v) => updateNestedField('deliverableFields', 'startDate', v)} label="Start Date" />
                      <SubCheckbox checked={deliverableFields?.dueDate} onChange={(v) => updateNestedField('deliverableFields', 'dueDate', v)} label="Due Date" />
                      <SubCheckbox checked={deliverableFields?.phase} onChange={(v) => updateNestedField('deliverableFields', 'phase', v)} label="Phase" />
                      <SubCheckbox checked={deliverableFields?.assignedResources} onChange={(v) => updateNestedField('deliverableFields', 'assignedResources', v)} label="Resources" />
                    </div>
                  </div>
                </div>

                {/* Gantt Chart Columns */}
                <div className={`rounded-lg border transition-colors ${(viewType === 'gantt' || viewType === 'gantt-resources') ? 'border-primary/30 bg-primary/5' : 'border-border opacity-60'}`}>
                  <div className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon name="BarChart3" size={14} className="text-primary flex-shrink-0" />
                      <p className="text-sm font-medium text-foreground">Gantt Chart Columns</p>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Columns to include in the <span className="font-medium">Gantt Chart</span> and <span className="font-medium">Gantt &amp; Resources</span> exports
                      {viewType !== 'gantt' && viewType !== 'gantt-resources' && (
                        <span className="ml-1 italic">(select Gantt view type to apply)</span>
                      )}
                    </p>
                    <div className="grid grid-cols-3 gap-x-4 gap-y-1.5">
                      <SubCheckbox checked={ganttColumns?.name ?? true} onChange={(v) => updateNestedField('ganttColumns', 'name', v)} label="Name" />
                      <SubCheckbox checked={ganttColumns?.startDate ?? true} onChange={(v) => updateNestedField('ganttColumns', 'startDate', v)} label="Start Date" />
                      <SubCheckbox checked={ganttColumns?.endDate ?? true} onChange={(v) => updateNestedField('ganttColumns', 'endDate', v)} label="End Date" />
                      <SubCheckbox checked={ganttColumns?.status ?? true} onChange={(v) => updateNestedField('ganttColumns', 'status', v)} label="Status" />
                      <SubCheckbox checked={ganttColumns?.phase ?? true} onChange={(v) => updateNestedField('ganttColumns', 'phase', v)} label="Phase" />
                      <SubCheckbox checked={ganttColumns?.assignedResources ?? false} onChange={(v) => updateNestedField('ganttColumns', 'assignedResources', v)} label="Resources" />
                      <SubCheckbox checked={ganttColumns?.duration ?? false} onChange={(v) => updateNestedField('ganttColumns', 'duration', v)} label="Duration" />
                      <SubCheckbox checked={ganttColumns?.percentComplete ?? false} onChange={(v) => updateNestedField('ganttColumns', 'percentComplete', v)} label="% Complete" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3: View Type */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">3</span>
                View Type
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {viewOptions?.map((opt) => (
                  <button
                    key={opt?.value}
                    onClick={() => updateSettings({ viewType: opt?.value })}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                      viewType === opt?.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                    }`}
                  >
                    <Icon name={opt?.icon} size={18} className={viewType === opt?.value ? 'text-primary' : 'text-muted-foreground'} />
                    <div>
                      <p className={`text-sm font-medium ${viewType === opt?.value ? 'text-primary' : 'text-foreground'}`}>{opt?.label}</p>
                      <p className="text-xs text-muted-foreground">{opt?.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 4: Task Detail Level */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">4</span>
                Task Detail Level
              </h3>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => updateSettings({ taskDetail: 'all' })}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                    taskDetail === 'all' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                  }`}
                >
                  <Icon name="ListTree" size={18} className={taskDetail === 'all' ? 'text-primary' : 'text-muted-foreground'} />
                  <div>
                    <p className={`text-sm font-medium ${taskDetail === 'all' ? 'text-primary' : 'text-foreground'}`}>Tasks & Subtasks</p>
                    <p className="text-xs text-muted-foreground">Include all tasks and subtasks under deliverables</p>
                  </div>
                </button>
                <button
                  onClick={() => updateSettings({ taskDetail: 'deliverables' })}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                    taskDetail === 'deliverables' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                  }`}
                >
                  <Icon name="CheckSquare" size={18} className={taskDetail === 'deliverables' ? 'text-primary' : 'text-muted-foreground'} />
                  <div>
                    <p className={`text-sm font-medium ${taskDetail === 'deliverables' ? 'text-primary' : 'text-foreground'}`}>Deliverables Only</p>
                    <p className="text-xs text-muted-foreground">Top-level deliverables only</p>
                  </div>
                </button>
              </div>
            </div>

            {/* Step 5: Gantt Timeline Scale */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">5</span>
                Gantt Timeline Scale
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {scaleOptions?.map((opt) => (
                  <button
                    key={opt?.value}
                    onClick={() => updateSettings({ ganttScale: opt?.value })}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all ${
                      ganttScale === opt?.value
                        ? 'border-primary bg-primary/5 text-primary' :'border-border hover:border-primary/40 text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name={opt?.value === 'daily' ? 'Calendar' : opt?.value === 'weekly' ? 'CalendarDays' : 'CalendarRange'} size={18} />
                    <span className="text-sm font-medium">{opt?.label}</span>
                    <span className="text-xs text-center opacity-70">{opt?.description}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 6: Gantt Date Range */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">6</span>
                Gantt Chart Date Range
              </h3>
              <p className="text-xs text-muted-foreground mb-3">
                All rows are always included. This controls the <span className="font-medium">visible date window</span> of the Gantt chart graphic.
              </p>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {[
                  { value: '30-days', label: '30 Days', icon: 'Calendar', description: 'Next 30 days from today' },
                  { value: '60-days', label: '60 Days', icon: 'Calendar', description: 'Next 60 days from today' },
                  { value: '90-days', label: '90 Days', icon: 'CalendarDays', description: 'Next 90 days from today' },
                  { value: 'whole-project', label: 'Whole Project', icon: 'CalendarRange', description: 'Full project date span' },
                  { value: 'custom', label: 'Custom Range', icon: 'CalendarClock', description: 'Pick start & end date' },
                ]?.map((opt) => (
                  <button
                    key={opt?.value}
                    onClick={() => updateSettings({ ganttDateRange: opt?.value })}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                      ganttDateRange === opt?.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                    }`}
                  >
                    <Icon name={opt?.icon} size={16} className={ganttDateRange === opt?.value ? 'text-primary' : 'text-muted-foreground'} />
                    <div>
                      <p className={`text-sm font-medium ${ganttDateRange === opt?.value ? 'text-primary' : 'text-foreground'}`}>{opt?.label}</p>
                      <p className="text-xs text-muted-foreground">{opt?.description}</p>
                    </div>
                  </button>
                ))}
              </div>
              {ganttDateRange === 'custom' && (
                <div className="flex items-center gap-3 p-3 rounded-lg border border-primary/30 bg-primary/5">
                  <div className="flex-1">
                    <label className="block text-xs text-muted-foreground mb-1">Start Date</label>
                    <input
                      type="date"
                      value={ganttDateRangeCustomStart}
                      onChange={(e) => updateSettings({ ganttDateRangeCustomStart: e?.target?.value })}
                      className="w-full text-sm bg-white border border-border rounded-md px-2 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div className="flex-shrink-0 mt-4">
                    <Icon name="ArrowRight" size={16} className="text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-muted-foreground mb-1">End Date</label>
                    <input
                      type="date"
                      value={ganttDateRangeCustomEnd}
                      onChange={(e) => updateSettings({ ganttDateRangeCustomEnd: e?.target?.value })}
                      className="w-full text-sm bg-white border border-border rounded-md px-2 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Step 7: Gantt Chart Colors */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">7</span>
                Gantt Chart Colors
              </h3>
              <div className="rounded-lg border border-border p-3 space-y-3">
                <p className="text-xs text-muted-foreground">Customize the bar colors for each item type in the Gantt chart</p>
                <ColorPickerRow label="Phases" colorKey="phase" previewColor={ganttColors?.phase || '#166534'} />
                <ColorPickerRow label="Deliverables" colorKey="deliverable" previewColor={ganttColors?.deliverable || '#16a34a'} />
                <ColorPickerRow label="Tasks" colorKey="task" previewColor={ganttColors?.task || '#fb923c'} />
                <ColorPickerRow label="Subtasks" colorKey="subtask" previewColor={ganttColors?.subtask || '#a78bfa'} />
                <button
                  onClick={() => updateSettings({ ganttColors: DEFAULT_SETTINGS?.ganttColors })}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 mt-1"
                >
                  <Icon name="RotateCcw" size={11} />
                  Reset to defaults
                </button>
              </div>
            </div>

            {/* Export Summary */}
            <div className="bg-muted/50 rounded-lg p-3 border border-border">
              <p className="text-xs text-muted-foreground font-medium mb-1">Export Summary</p>
              <p className="text-xs text-foreground">
                <span className="font-semibold">{formatOptions?.find((f) => f?.value === exportFormat)?.label}</span>
                {' · '}
                {viewOptions?.find((v) => v?.value === viewType)?.label}
                {' · '}
                {[includeOverview && 'Overview', includePhases && 'Phases']?.filter(Boolean)?.join(', ') || 'No header data'}
                {' · '}
                {taskDetail === 'all' ? 'Tasks & Subtasks' : 'Deliverables Only'}
                {` · ${scaleOptions?.find((s) => s?.value === ganttScale)?.label} scale`}
                {(viewType === 'gantt' || viewType === 'gantt-resources') && (
                  <span>
                    {' · '}
                    {ganttDateRange === 'whole-project' ? 'Whole project' :
                     ganttDateRange === 'custom' ? `Custom range` :
                     ganttDateRange}
                    {' range'}
                  </span>
                )}
              </p>
              {settingsLoaded && user?.id && (
                <p className="text-xs text-primary/60 mt-1 flex items-center gap-1">
                  <Icon name="Check" size={11} />
                  Settings auto-saved for this project
                </p>
              )}
            </div>
          </div>

          {/* Preview panel */}
          {showPreview && (
            <div className="w-96 flex-shrink-0 flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30 flex-shrink-0">
                <span className="text-sm font-medium text-foreground flex items-center gap-1.5">
                  <Icon name="Eye" size={14} className="text-primary" />
                  Preview
                </span>
                <button
                  onClick={generatePreview}
                  disabled={isGeneratingPreview}
                  className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
                >
                  <Icon name="RefreshCw" size={12} className={isGeneratingPreview ? 'animate-spin' : ''} />
                  Refresh
                </button>
              </div>
              <div className="flex-1 overflow-auto p-3 bg-muted/20">
                {isGeneratingPreview ? (
                  <div className="flex flex-col items-center justify-center h-40 gap-3">
                    <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                    <p className="text-xs text-muted-foreground">Generating preview…</p>
                  </div>
                ) : previewUrl ? (
                  <div className="space-y-2">
                    <img
                      src={previewUrl}
                      alt="Export preview showing the project data layout"
                      className="w-full rounded-lg border border-border shadow-sm"
                    />
                    <p className="text-xs text-muted-foreground text-center">
                      Preview approximation · Click Refresh after changing settings
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-40 gap-3 text-center">
                    <Icon name="Image" size={28} className="text-muted-foreground/40" />
                    <p className="text-xs text-muted-foreground">Click Refresh to generate a preview of your export</p>
                    <button
                      onClick={generatePreview}
                      className="text-xs text-primary hover:underline"
                    >
                      Generate Preview
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border flex-shrink-0">
          <Button variant="outline" onClick={onClose} disabled={isExporting}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={isExporting || (!includeOverview && !includePhases && deliverables?.length === 0)}
            iconName={isExporting ? undefined : 'Download'}
            iconPosition="left"
          >
            {isExporting ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Exporting…
              </span>
            ) : (
              `Export as ${formatOptions?.find((f) => f?.value === exportFormat)?.label}`
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
